export type CenterCallback = (center: google.maps.LatLngLiteral) => void;

class MapService {
  private static instance: MapService;
  private centerSubscribers: CenterCallback[] = [];
  private center!: google.maps.LatLngLiteral;

  private constructor(defaultCenter: google.maps.LatLngLiteral) {
    this.center = defaultCenter;
  }

  public static getInstance(defaultCenter: google.maps.LatLngLiteral): MapService {
    if (!MapService.instance) {
      MapService.instance = new MapService(defaultCenter);
    }
    return MapService.instance;
  }

  public getCenter(): google.maps.LatLngLiteral {
    return this.center;
  }

  public setCenter(center: google.maps.LatLngLiteral): void {
    this.center = center;
    this.notifyCenterSubscribers();
  }

  /**
   * Subscribe to changes on the center.
   * Notifies immediately with the current center.
   * Accepts an optional AbortSignal to automatically unsubscribe when aborted.
   * @param callback A function that will be called whenever the center changes.
   * @param notify Optional boolean to control immediate notification. Defaults to true.
   * @param options Optional object containing an AbortSignal.
   * @returns An unsubscribe function.
   */
  public subscribeToCenter(callback: CenterCallback, notify:boolean, options?: { signal?: AbortSignal }): () => void {
    this.centerSubscribers.push(callback);
    if (notify) {
      callback(this.center);
    }

    let abortHandler: () => void;

    const unsubscribe = () => {
      this.centerSubscribers = this.centerSubscribers.filter(cb => cb !== callback);
      if (options?.signal && abortHandler) {
        options.signal.removeEventListener('abort', abortHandler);
      }
    };

    if (options?.signal) {
      if (options.signal.aborted) {
        unsubscribe();
      } else {
        abortHandler = () => {
          unsubscribe();
        };
        options.signal.addEventListener('abort', abortHandler);
      }
    }
    return unsubscribe;
  }

  /**
   * Calls all center subscribers with the current center.
   */
  private notifyCenterSubscribers(): void {
    this.centerSubscribers.forEach(callback => callback(this.center));
  }
}

export const mapService = MapService.getInstance({ lat: 0, lng: 0 });
